import { useCallback, useEffect, useState } from "react";
import API from "../../services/api";
import ThreadPanel from "../ContentWriter/OrderBooking/components/ThreadPanel";
import useAdminCreativeThread from "../ContentWriter/OrderBooking/admin/hooks/useAdminCreativeThread";
import { subTypeLabel } from "../ContentWriter/OrderBooking/admin/config";

/// Order -> its creatives -> chat. Same thread (and endpoints/socket) the CST DetailDrawer
/// Chat tab uses; the backend scopes financers to orders routed to them.
const markRead = (bsc_id) =>
  API({ url: "/slotbooking/mark-creative-read", method: "POST", responseType: "json", data: { bsc_id } }).catch(() => {});

const UnreadBadge = ({ count }) =>
  count > 0 ? (
    <span className="ml-auto min-w-[18px] rounded-full bg-green-500 px-1.5 text-center text-[10px] font-semibold leading-[18px] text-white">
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

const CreativeThread = ({ bscId }) => {
  const { logs, loading, error, reload, sendMessage, sendAttachment } = useAdminCreativeThread(bscId);

  // Read on open and again on leave, so messages that arrived while open don't count.
  useEffect(() => {
    markRead(bscId);
    return () => markRead(bscId);
  }, [bscId]);

  return (
    <ThreadPanel
      logs={logs}
      loading={loading}
      error={error}
      onRetry={reload}
      canSend
      onSend={sendMessage}
      onSendAttachment={sendAttachment}
      onZoom={(url) => window.open(url, "_blank")}
    />
  );
};

/// `onClose(remainingUnread)` lets the queue update the order's badge without a refetch.
const OrderCreativesChat = ({ row, onClose }) => {
  const [creatives, setCreatives] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let live = true;
    API({ url: "/slotbooking/get-order-creatives", method: "POST", responseType: "json", data: { slot_id: row.order_id } })
      .then((res) => {
        if (!live) return;
        if (res.code !== 200) return setFailed(true);
        const list = res.data?.data || [];
        setCreatives(list);
        setSelected(list[0]?.bsc_id ?? null);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [row.order_id]);

  // Opening a creative reads it — clear its badge locally.
  useEffect(() => {
    if (selected == null) return;
    setCreatives((prev) => prev?.map((c) => (c.bsc_id === selected ? { ...c, unread_count: 0 } : c)));
  }, [selected]);

  const close = useCallback(() => {
    const remaining = (creatives || []).reduce((sum, c) => sum + (c.bsc_id === selected ? 0 : Number(c.unread_count) || 0), 0);
    onClose(creatives ? remaining : undefined);
  }, [creatives, selected, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="flex h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:h-[85vh] sm:rounded-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Creatives chat</h3>
            <p className="mt-0.5 truncate font-mono text-xs text-gray-500">
              Order #{row.order_ref || row.order_id}
              {row.brand_name ? ` · ${row.brand_name}` : ""}
            </p>
          </div>
          <button
            onClick={close}
            aria-label="Close"
            className="-mr-1 rounded-lg p-1 text-2xl leading-none text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            &times;
          </button>
        </div>

        {failed ? (
          <p className="px-5 py-10 text-center text-sm text-red-600">Unable to load this order's creatives.</p>
        ) : creatives === null ? (
          <p className="px-5 py-10 text-center text-sm text-gray-400">Loading creatives...</p>
        ) : creatives.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-gray-500">This order has no creatives.</p>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
            <ul className="flex shrink-0 gap-2 overflow-x-auto border-b border-gray-100 p-3 sm:w-60 sm:flex-col sm:gap-1 sm:overflow-y-auto sm:border-b-0 sm:border-r">
              {creatives.map((c) => (
                <li key={c.bsc_id} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => setSelected(c.bsc_id)}
                    aria-pressed={selected === c.bsc_id}
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors ${
                      selected === c.bsc_id ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-gray-50"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block max-w-[180px] truncate text-sm font-medium text-gray-800">
                        {c.creative_name || `Creative #${c.bsc_id}`}
                      </span>
                      <span className="block text-xs text-gray-500">{subTypeLabel(c.sub_type)}</span>
                    </span>
                    <UnreadBadge count={Number(c.unread_count) || 0} />
                  </button>
                </li>
              ))}
            </ul>
            <div className="min-h-0 flex-1">
              {selected != null && <CreativeThread key={selected} bscId={selected} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderCreativesChat;
